import { CheckCircleOutline, Launch, LocalShipping, PendingOutlined, Warning } from "@mui/icons-material"
import { CardActions, Grid2 as Grid, Tooltip, Typography } from "@mui/material"
import { blue, green, grey } from "@mui/material/colors"
import { ReactNode } from "react"
import { NexCard, NexCardActionButton, NexCardContent, NexCardHeader } from "../../components/NexCard"
import { NexButtonLink } from "../../components/NexRouterButton"
import { useMissions } from "../../hooks/useMissions"
import { MissionShippingInfoWithReceiptDto, MissionShippingInfoWrapper } from "../../models/missions/ShippingInfo"
import { routes } from "../../routing/NexRoutes"
import { shippingInfoMissingFields } from "../../utils/MissionUtils"
import { TimeUtilsDateFns } from "../../utils/TimeUtils"
import { useShippingInfoStore } from "../../zstore/ShippingInfoStore"
import { missionDisplayNames } from "./model"

interface MissionShippingInfoCardProps {
    shippingInfoWrapper: MissionShippingInfoWrapper
}

export const ShippingInfoCard = ({ shippingInfoWrapper }: MissionShippingInfoCardProps) => {
    const { missions } = useMissions()
    const { isVendorFlow } = missions
    const setSelectedShippingWrapperToEdit = useShippingInfoStore((state) => state.setSelectedShippingWrapperToEdit)

    const shippingInfo = shippingInfoWrapper.shipping_info
    const missingFields = shippingInfoMissingFields(shippingInfo, isVendorFlow)
    const missingFieldDisplayNames = missingFields.map((field) => missionDisplayNames[field])
    return (
        <NexCard>
            <ShippingInfoCardMedia shippingInfo={shippingInfo} />
            <NexCardContent>
                <Typography variant="h6" component={"div"} sx={{ pb: 2 }}>
                    {shippingInfo.delivery_drive_name ?? "Unlabeled Drive"}
                </Typography>
                {/* TODO - make this a clickable link ? */}
                {shippingInfo.shipping_number && <Typography>{shippingInfo.shipping_number}</Typography>}
                <Typography>Sent to {shippingInfo.office}</Typography>
                {shippingInfo.raw_storage_size_gb && <Typography>{shippingInfo.raw_storage_size_gb} GB</Typography>}
            </NexCardContent>
            <CardActions sx={{ justifyContent: "flex-end" }}>
                {missingFields.length !== 0 && (
                    <Tooltip title={`${missingFieldDisplayNames.join(", ")} is required`}>
                        <Warning color="error" />
                    </Tooltip>
                )}
                <NexCardActionButton onClick={() => setSelectedShippingWrapperToEdit(shippingInfoWrapper)}>
                    Edit
                </NexCardActionButton>
            </CardActions>
        </NexCard>
    )
}

const ShippingInfoCardMedia = ({ shippingInfo }: { shippingInfo: MissionShippingInfoWithReceiptDto }) => {
    let cardMedia: ReactNode = (
        <NexCardHeader
            Icon={PendingOutlined}
            backgroundColor={grey[500]}
            title={
                <Typography sx={{ color: grey[100], pr: 2 }} variant="h6">
                    No Shipping Info Yet...
                </Typography>
            }
        />
    )

    if (shippingInfo.shipping_number && !shippingInfo.data_receipt) {
        cardMedia = (
            // TODO - we want to add a map and a way to track the shipping number
            //  - until then this is a temporary placeholder
            <NexCardHeader
                Icon={LocalShipping}
                backgroundColor={blue[500]}
                title={
                    <Typography sx={{ color: grey[100], pr: 2 }} variant="h6">
                        In Transit...
                    </Typography>
                }
            />
        )
    } else if (shippingInfo.data_receipt) {
        const dataReceipt = shippingInfo.data_receipt
        cardMedia = (
            <NexCardHeader
                Icon={CheckCircleOutline}
                backgroundColor={green.A700}
                title={
                    <Grid color={grey[100]}>
                        <Grid container sx={{ alignItems: "center" }}>
                            <Grid>
                                <Typography variant="h5">Received</Typography>
                            </Grid>
                            <Grid display={"none"}>
                                <NexButtonLink
                                    newWindow={true}
                                    to={routes.editDataReceipt(`${shippingInfo.data_receipt.id}`)}
                                    buttonProps={{ sx: { p: 0, color: grey[200] } }}
                                >
                                    <Launch />
                                </NexButtonLink>
                            </Grid>
                        </Grid>
                        {dataReceipt.received_by_name && <Typography>By {dataReceipt.received_by_name}</Typography>}
                        <Typography>{TimeUtilsDateFns.formatDate(new Date(dataReceipt.date_received))}</Typography>
                        <Typography>{dataReceipt.office}</Typography>
                    </Grid>
                }
            />
        )
    }
    return cardMedia
}
